import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getToken } from "../../utils/auth";

import {
    faClock,
    faUserCheck,
    faExclamationTriangle,
    faHistory,
    faCalendarAlt,
    faSpinner,
    faCircleExclamation,
    faXmark,
    faCheck
} from "@fortawesome/free-solid-svg-icons";

const CRMTablaAlertas = ({ onGestionarClick, onVerHistorialClick }) => {
    const [listaAlertas, setListaAlertas] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAlertas();
    }, []);

    const fetchAlertas = async () => {
        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_MIS_ALERTAS, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token }),
            });

            if (response.ok) {
                const data = await response.json();

                const soloActivas = data.filter(
                    item => item.alertaActiva === true || item.alertaActiva === 1
                );

                const datosFormateados = soloActivas.map((item) => {
                    let nombre = `CUIT: ${item.cuit}`;
                    // SOLO telefono de la tabla (item.telefono). Si no hay, vacío.
                    let telefono = item?.telefono ? String(item.telefono).trim() : "";
                    let nota = item.observacion;

                    if (item.observacion && (item.observacion.includes("[NUEVO]") || item.observacion.includes("Prospecto:"))) {
                        try {
                            let textoLimpio = item.observacion
                                .replace("[NUEVO]", "")
                                .replace("Prospecto:", "")
                                .trim();

                            const partes = textoLimpio.split("|");
                            if (partes.length >= 1) nombre = partes[0].trim();
                            // NO usar partes[1] para telefono
                            if (partes.length >= 3) nota = partes.slice(2).join(" ").trim();
                        } catch (e) { }
                    }

                    return {
                        id: item.id,
                        cuit: item.cuit,
                        fechaVencimiento: item.fechaProximoContacto,
                        nombreCliente: nombre,
                        telefono, // solo DB
                        titulo: item.reglaAplicada ? item.reglaAplicada.aliasVista : "Sin Regla",
                        mensaje: nota,
                        idAlta: item.idAlta
                    };
                });

                setListaAlertas(ordenarPorPrioridad(datosFormateados));
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    // 🔥 ORDEN: vencidos → hoy → próximos → null al final
    const ordenarPorPrioridad = (lista) => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        return [...lista].sort((a, b) => {
            if (!a.fechaVencimiento && !b.fechaVencimiento) return 0;
            if (!a.fechaVencimiento) return 1;
            if (!b.fechaVencimiento) return -1;

            const fa = new Date(a.fechaVencimiento);
            const fb = new Date(b.fechaVencimiento);
            fa.setHours(0, 0, 0, 0);
            fb.setHours(0, 0, 0, 0);

            const aVencido = fa < hoy;
            const bVencido = fb < hoy;

            if (aVencido && !bVencido) return -1;
            if (!aVencido && bVencido) return 1;

            return fa - fb;
        });
    };

    const cerrarGestion = async (alerta) => {
        const token = getToken();

        const confirm = await Swal.fire({
            title: "Cerrar gestión",
            text: "Esta gestión dejará de aparecer como prioritaria.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Cerrar",
            cancelButtonText: "Cancelar"
        });

        if (!confirm.isConfirmed) return;

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_CERRAR_GESTION, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdSeguimiento: alerta.id
                })
            });

            if (response.ok) {
                Swal.fire("Cerrado", "La gestión fue cerrada", "success");
                fetchAlertas();
            } else {
                Swal.fire("Error", "No se pudo cerrar la gestión", "error");
            }
        } catch {
            Swal.fire("Error", "Error de conexión", "error");
        }
    };

    // ✅ Nuevo: derivar a BIENVENIDA (POSVENTA) con el tilde
    const marcarBienvenida = async (alerta) => {
        const token = getToken();

        const confirm = await Swal.fire({
            title: "Derivar a POSVENTA",
            text: "Se marcará esta gestión con la regla BIENVENIDA.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar"
        });

        if (!confirm.isConfirmed) return;

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_MARCAR_BIENVENIDA, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdSeguimiento: alerta.id
                })
            });

            if (response.ok) {
                Swal.fire("Éxito", "Caso derivado a BIENVENIDA", "success");
                fetchAlertas();
            } else {
                let msg = "No se pudo derivar a BIENVENIDA";
                try {
                    const err = await response.json();
                    if (err?.mensaje) msg = err.mensaje;
                } catch (_) { }
                Swal.fire("Error", msg, "error");
            }
        } catch {
            Swal.fire("Error", "Error de conexión", "error");
        }
    };

    const obtenerInfoVencimiento = (fechaStr) => {
        if (!fechaStr) {
            return {
                etiqueta: "PENDIENTE",
                clase: "text-muted",
                icono: faClock,
                fecha: "--"
            };
        }

        const fechaVenc = new Date(fechaStr);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        fechaVenc.setHours(0, 0, 0, 0);

        const diffTime = fechaVenc - hoy;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const fechaFormato = fechaVenc.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });

        if (diffDays < 0) {
            return {
                etiqueta: "VENCIDO",
                subtexto: `Hace ${Math.abs(diffDays)} d.`,
                clase: "text-danger fw-bold",
                bg: "bg-danger bg-opacity-10",
                icono: faCircleExclamation,
                fecha: fechaFormato
            };
        } else if (diffDays === 0) {
            return {
                etiqueta: "HOY",
                subtexto: "Urgente",
                clase: "text-success fw-bold",
                bg: "bg-success bg-opacity-10",
                icono: faClock,
                fecha: fechaFormato
            };
        } else {
            return {
                etiqueta: `EN ${diffDays} DÍAS`,
                subtexto: "Programado",
                clase: "text-primary fw-bold",
                bg: "",
                icono: faCalendarAlt,
                fecha: fechaFormato
            };
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <FontAwesomeIcon icon={faSpinner} spin /> Cargando alertas...
            </div>
        );
    }

    if (!listaAlertas.length) {
        return (
            <div className="text-center py-5 text-muted">
                <h4>🎉 Todo al día</h4>
            </div>
        );
    }

    return (
        <div className="table-responsive shadow-sm rounded border">
            <table className="table table-hover align-middle mb-0 bg-white">
                <thead className="table-dark">
                    <tr>
                        <th className="text-center" style={{ width: "12%" }}>ESTADO</th>
                        <th className="text-center" style={{ width: "8%" }}>FECHA</th>
                        <th style={{ width: "22%" }}>CLIENTE / CUIT</th>
                        <th style={{ width: "38%" }}>ÚLTIMA ACCIÓN / NOTA</th>
                        <th className="text-end px-4" style={{ width: "20%" }}>ACCIONES</th>
                    </tr>
                </thead>
                <tbody>
                    {listaAlertas.map((alerta) => {
                        const info = obtenerInfoVencimiento(alerta.fechaVencimiento);
                        return (
                            <tr key={alerta.id} className={info.bg}>
                                <td className="text-center border-end">
                                    <div className={`${info.clase} d-flex flex-column align-items-center`}>
                                        <span style={{ fontSize: "0.9rem" }}>
                                            <FontAwesomeIcon icon={info.icono} className="me-1" />
                                            {info.etiqueta}
                                        </span>
                                        <small className="text-muted fw-normal" style={{ fontSize: "0.65rem" }}>
                                            {info.subtexto}
                                        </small>
                                    </div>
                                </td>
                                <td className="text-center border-end fw-bold text-secondary">
                                    {info.fecha}
                                </td>
                                <td className="ps-3">
                                    <div className="fw-bold text-dark">{alerta.nombreCliente}</div>
                                    <div className="text-muted small">CUIT: {alerta.cuit || ""}</div>
                                    <div className="text-muted small">TEL: {alerta.telefono || ""}</div>
                                </td>
                                <td>
                                    <span className="badge bg-secondary mb-1">{alerta.titulo}</span>
                                    <div className="text-muted text-truncate" style={{ maxWidth: "300px", fontSize: "0.85rem" }}>
                                        {alerta.mensaje}
                                    </div>
                                </td>
                                <td className="text-end px-4" style={{ whiteSpace: "nowrap" }}>
                                    <div className="d-inline-flex align-items-center">
                                        <button
                                            className="btn btn-light btn-sm me-2 border"
                                            onClick={() => onVerHistorialClick(alerta)}
                                        >
                                            <FontAwesomeIcon icon={faHistory} />
                                        </button>

                                        <button
                                            className="btn btn-success btn-sm me-2"
                                            onClick={() => onGestionarClick(alerta)}
                                        >
                                            <FontAwesomeIcon icon={faUserCheck} />
                                        </button>
                                        <button
                                            className="btn btn-outline-success btn-sm me-2"
                                            title="Derivar a BIENVENIDA"
                                            onClick={() => marcarBienvenida(alerta)}
                                            disabled={false}
                                        >
                                            <FontAwesomeIcon icon={faCheck} />
                                        </button>
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => cerrarGestion(alerta)}
                                            title="Cerrar gestión"
                                        >
                                            <FontAwesomeIcon icon={faXmark} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CRMTablaAlertas;
